// src/core/logger.ts - Modified implementation
import { createConsola } from "consola";
import type {
  ConsolaInstance,
  PromptOptions,
  InputLogObject,
  ConsolaReporter,
  LogObject,
  ConsolaOptions,
  LogType,
} from "consola";
import { DEFAULT_OPTIONS } from "./constants.js";
import type { LoggerOptions, LogContext, Reporter } from "./types.js";
import {
  LoggerError,
  SerializationError,
  ConfigurationError,
  defaultErrorHandler,
  ErrorHandler,
} from "./errors.js";
import { LOG_LEVELS, type LogLevel } from "./constants.js";

// Add debug logging
const DEBUG =
  process.env.DEBUG === "true" && process.env.NODE_ENV === "development";

function debugLog(...args: any[]) {
  if (DEBUG) {
    console.log("[LOGGER_DEBUG]", ...args);
  }
}

type SelectOption = {
  label: string;
  value: string;
  hint?: string;
};

export class Logger {
  private static instance: Logger;
  consola: ConsolaInstance;

  private reporters: Reporter[] = [];

  private options: LoggerOptions;

  constructor(options: LoggerOptions = {}, injectedConsola?: ConsolaInstance) {
    try {
      // Pass everything directly to Consola
      this.consola =
        injectedConsola ||
        createConsola({
          level: options.level ?? DEFAULT_OPTIONS.level,
          reporters: options.reporters || DEFAULT_OPTIONS.reporters,
          formatOptions: {
            ...DEFAULT_OPTIONS.formatOptions,
            ...options.formatOptions,
          },
        });

      // Store context and other options for our own use
      this.options = {
        ...DEFAULT_OPTIONS,
        ...options,
        context: options.context || {},
      };
    } catch (error) {
      const configError = new ConfigurationError(
        "Failed to initialize logger",
        error instanceof Error ? error : undefined
      );
      throw configError;
    }
  }

  private shouldLog(level: keyof typeof LOG_LEVELS): boolean {
    const logLevel = LOG_LEVELS[level];
    const currentLevel = this.consola.level;

    // Handle special cases
    if (currentLevel === LOG_LEVELS.SILENT) {
      return false;
    }
    if (currentLevel === LOG_LEVELS.VERBOSE) {
      return true;
    }

    // For all other levels, only log if the message level is less than or equal to current level
    return logLevel <= currentLevel;
  }

  public static getInstance(options?: LoggerOptions): Logger {
    debugLog("getInstance called with options:", options);
    if (!Logger.instance) {
      debugLog("Creating new Logger instance");
      Logger.instance = new Logger(options);
    } else {
      debugLog("Returning existing Logger instance");
    }
    return Logger.instance;
  }

  /**
   * Create a new logger with additional context
   */
  withContext(context: Record<string, any>): Logger {
    debugLog(`withContext called with: ${JSON.stringify(context)}`);
    debugLog("Creating new logger with merged context");

    // Create new options with merged context
    const newOptions: LoggerOptions = {
      level: this.options.level,
      formatOptions: { ...this.options.formatOptions },
      context: {
        ...this.options.context,
        ...context,
      },
    };

    // Create a new logger instance with the merged context
    const newLogger = new Logger(newOptions);

    debugLog(
      `Setting reporters on new logger: ${JSON.stringify(this.reporters)}`
    );

    // Ensure reporters are properly set on the new logger
    newLogger.reporters = this.reporters as ConsolaReporter[];

    return newLogger;
  }

  public setLevel(level: number): void {
    this.consola.level = level;
    this.options.level = level;
  }

  public async end(): Promise<void> {
    await Promise.all(
      this.reporters.map((reporter) => reporter.end?.()).filter(Boolean)
    );
  }

  public prompt(
    message: string,
    options: PromptOptions | undefined
  ): Promise<string | boolean | SelectOption | (string | SelectOption)[]> {
    return this.consola.prompt(message, options);
  }

  /**
   * Debug level logging - improved to handle console-like inputs
   * Can be called like: logger.debug("Message", { data }, error)
   */
  debug(message: any, ...args: any[]): void {
    if (!this.shouldLog("DEBUG")) return;

    // For plain string message with no args, pass just the message
    if (typeof message === "string" && args.length === 0) {
      this.consola.debug(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.debug(message, ...args);
    }
  }

  /**
   * Info level logging - improved to handle console-like inputs
   * Can be called like: logger.info("Message", { data }, error)
   */
  info(message: any, ...args: any[]): void {
    if (!this.shouldLog("INFO")) return;
    // For plain string message with no args, pass just the message
    if (typeof message === "string" && args.length === 0) {
      this.consola.info(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.info(message, ...args);
    }
  }

  /**
   * Success level logging - improved to handle console-like inputs
   * Can be called like: logger.success("Message", { data }, error)
   */
  success(message: any, ...args: any[]): void {
    if (!this.shouldLog("SUCCESS")) return;

    // For plain string message with no args, pass just the message
    if (typeof message === "string" && args.length === 0) {
      this.consola.success(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.success(message, ...args);
    }
  }

  /**
   * Warning level logging - improved to handle console-like inputs
   * Can be called like: logger.warn("Message", { data }, error)
   */
  warn(message: any, ...args: any[]): void {
    if (!this.shouldLog("WARN")) return;

    // For plain string message with no args, pass just the message
    if (typeof message === "string" && args.length === 0) {
      this.consola.warn(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.warn(message, ...args);
    }
  }

  /**
   * Error level logging - improved to handle console-like inputs
   * Can be called like: logger.error("Message", error, { data })
   * or: logger.error(error) or logger.error("Message", { error })
   */
  error(message: any, ...args: any[]): void {
    if (!this.shouldLog("ERROR")) return;

    if (typeof message === "string" && args.length === 0) {
      this.consola.error(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.error(message, ...args);
    }
  }

  /**
   * Fatal level logging - improved to handle console-like inputs
   * Can be called like: logger.fatal("Message", error, { data })
   */
  fatal(message: any, ...args: any[]): void {
    if (!this.shouldLog("FATAL")) return;

    if (typeof message === "string" && args.length === 0) {
      this.consola.fatal(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.fatal(message, ...args);
    }
  }

  /**
   * Trace level logging - improved to handle console-like inputs
   * Can be called like: logger.trace("Message", { data }, error)
   */
  trace(message: any, ...args: any[]): void {
    if (!this.shouldLog("TRACE")) return;

    // For plain string message with no args, pass just the message
    if (typeof message === "string" && args.length === 0) {
      this.consola.trace(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.trace(message, ...args);
    }
  }

  /**
   * Generic logging method - improved to handle console-like inputs
   * Can be called like: logger.log("Message", { data }, error)
   */
  log(message: any, ...args: any[]): void {
    if (!this.shouldLog("LOG")) return;

    // For plain string message with no args, pass just the message
    if (typeof message === "string" && args.length === 0) {
      this.consola.log(message);
    } else {
      // Otherwise use consola with all arguments
      this.consola.log(message, ...args);
    }
  }
}
